import React, { useContext, useMemo, useState } from "react";
import { Box, Button, IconButton, TextField, Typography } from "@mui/material";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import { useLocation, useNavigate } from "react-router-dom";
import AuthLayout from "./AuthLayout";
import sideImg from "../../assets/auth-side.png";
import { AuthContext } from "../../auth/AuthContext";

function useQuery() {
  const { search } = useLocation();
  return useMemo(() => new URLSearchParams(search), [search]);
}

export default function AuthSignup() {
  const navigate = useNavigate();
  const q = useQuery();
  const presetEmail = q.get("email") || "";

  const { register } = useContext(AuthContext);

  const [fullName, setFullName] = useState("");
  const [email, setEmail] = useState(presetEmail);
  const [password, setPassword] = useState("");
  const [verify, setVerify] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");

  const onContinue = async () => {
    setError("");

    if (password !== verify) {
      setError("Passwords do not match");
      return;
    }

    setSubmitting(true);
    try {
      await register({ fullName, email, password });
      navigate("/");
    } catch (err) {
      setError(err?.response?.data?.message || "Sign up failed");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <AuthLayout sideImage={sideImg}>
      <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 1 }}>
        <IconButton onClick={() => navigate("/auth/email")} sx={{ ml: -1 }} aria-label="Back">
          <ArrowBackIcon />
        </IconButton>

        <Typography variant="h6">Finish Sign-Up</Typography>
      </Box>

      <Typography sx={{ color: "text.secondary", mb: 3, maxWidth: 420 }}>
        Once you have created the account you’ll be automatically logged in.
      </Typography>

      <Typography variant="subtitle2" sx={{ mb: 1 }}>
        Email (personal or work)
      </Typography>
      <TextField
        fullWidth
        placeholder="e.g. user@example.com"
        value={email}
        onChange={(e) => setEmail(e.target.value)}
        autoComplete="email"
      />

      <Typography variant="subtitle2" sx={{ mt: 2.5, mb: 1 }}>
        Full Name
      </Typography>
      <TextField
        fullWidth
        placeholder="Enter your name"
        value={fullName}
        onChange={(e) => setFullName(e.target.value)}
        autoComplete="name"
      />

      <Typography variant="subtitle2" sx={{ mt: 2.5, mb: 1 }}>
        Create Password
      </Typography>
      <TextField
        fullWidth
        placeholder="Enter password here"
        type="password"
        value={password}
        onChange={(e) => setPassword(e.target.value)}
        autoComplete="new-password"
      />

      <Typography variant="subtitle2" sx={{ mt: 2.5, mb: 1 }}>
        Verify Password
      </Typography>
      <TextField
        fullWidth
        placeholder="Re-enter password here"
        type="password"
        value={verify}
        onChange={(e) => setVerify(e.target.value)}
        autoComplete="new-password"
      />

      {error ? (
        <Typography sx={{ color: "error.main", mt: 1.5, fontSize: 13 }}>
          {error}
        </Typography>
      ) : null}

      <Button
        fullWidth
        variant="contained"
        disabled={submitting || !email || !fullName || !password || !verify}
        onClick={onContinue}
        sx={{ mt: 4, opacity: submitting ? 0.8 : 1 }}
      >
        {submitting ? "Creating account..." : "Continue"}
      </Button>
    </AuthLayout>
  );
}
